import { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled/macro';
import {
  Button,
  Card,
  Snackbar,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
import { Autocomplete } from 'components/molecules/Autocomplete';
import { useRetailStores } from 'providers/RetailStores';
import { publishMessage, useSubscribe } from 'helpers/messaging';
import type { Message } from 'helpers/messaging';

type RetailStoreOption = {
  value: string;
  label: string;
};

const RetailAdvisorDashboard = () => {
  const { data: retailList, isLoading } = useRetailStores();

  const savedLocation = useMemo(
    () => localStorage.getItem('retailStoreLocation'),
    [],
  );
  const savedName = useMemo(() => localStorage.getItem('retailStoreName'), []);

  const hasSavedStoreLocation = !!savedLocation && !!savedName;

  const retailStoreOptions: RetailStoreOption[] = retailList?.map((store) => ({
    value: store.accountId,
    label: `${store.name}`,
  })) ?? [{ value: '', label: '' }];

  const [retailStore, setRetailStore] = useState<RetailStoreOption | null>(
    hasSavedStoreLocation ? { value: savedLocation, label: savedName } : null,
  );

  const [isValueUpdated, setIsValueUpdated] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', type: 'success' });

  useEffect(() => {
    // This useEffect will be called exactly once when the component mounts
    publishMessage({
      type: 'EVENT:READY',
      source: 'Dashboard',
    });
  }, []);

  useSubscribe((message: Message) => {
    if (message.type === 'EVENT:GET_PARENT_RETAIL_STORE_DATA') {
      const isDifferent =
        message.payload.retailStoreLocation !== savedLocation ||
        message.payload.retailStoreName !== savedName;

      if (isDifferent) {
        // store the information in localStorage if it is different from the current store
        localStorage.setItem('channelType', message.payload.channelType);
        localStorage.setItem(
          'retailStoreLocation',
          message.payload.retailStoreLocation,
        );
        localStorage.setItem(
          'retailStoreName',
          message.payload.retailStoreName,
        );
        setRetailStore({
          value: message.payload.retailStoreLocation,
          label: message.payload.retailStoreName,
        });
        setIsValueUpdated(false);
      }
    }
  });

  const handleSave = () => {
    if (!retailStore?.value) {
      return;
    }

    try {
      // Cannot use localStorage and expect the same behavior as the previous Channel Selection because
      // this repo and the leads repo will be in different origins, so the localStorage will not be shared.
      // We will post a message to the parent window so that it can relay the message to the leads repo when needed.
      publishMessage({
        type: 'COMMAND:SET_RETAIL_STORE_LOCATION',
        source: 'Dashboard',
        payload: {
          retailStoreLocation: retailStore.value,
          retailStoreName: retailStore.label,
          channelType: 'Retail',
        },
      });

      // Save to localStorage for dashboard usage
      localStorage.setItem('retailStoreLocation', retailStore.value);
      localStorage.setItem('retailStoreName', retailStore.label);
      localStorage.setItem('channelType', 'Retail');

      setShowSnackbar({
        open: true,
        message: 'Store address was set successfully',
        type: 'success',
      });
      setIsValueUpdated(false);
    } catch (error) {
      setShowSnackbar({
        open: true,
        message:
          'Error setting store address. Please verify if you have local storage permissions or try again later.',
        type: 'error',
      });
    }
  };

  const handleClear = () => {
    setIsValueUpdated(false);
    setRetailStore(null);
  };

  const handleSelect = (option: RetailStoreOption) => {
    const isValueUpdated = retailStore?.value !== option.value;
    setIsValueUpdated(isValueUpdated);
    setRetailStore(option);
  };

  return (
    <CustomCard>
      <TitleContainer data-testid="retail-advisor-dashboard-title">
        <Typography color="#FFFFFF" variant="h1">
          Where are you today?
        </Typography>
        <Typography color="#FFFFFF">
          Select the retail store location where your shift is today. This
          important retail store location will be tagged to every lead you
          create today.
        </Typography>
      </TitleContainer>
      <LocationContainer>
        <CustomAutocomplete
          label="Retail store location"
          testId="RetailAdvisorDashboard-store-location-autocomplete"
          isLoading={isLoading}
          options={retailStoreOptions}
          value={retailStore?.label ?? ''}
          onClear={handleClear}
          onSelect={handleSelect}
          onChange={(value) => {
            if (value === '') {
              handleClear();
            }
          }}
          placeholder="Enter retail store location"
        />
        <CustomButton
          disabled={isLoading || !retailStore || !isValueUpdated}
          color="primary"
          size="md"
          onClick={handleSave}>
          Save
        </CustomButton>
      </LocationContainer>
      <Snackbar
        open={showSnackbar.open}
        onClose={() =>
          setShowSnackbar({ open: false, message: '', type: 'success' })
        }
        message={showSnackbar.message}
        type={showSnackbar.type}
        colorScheme="dark"
      />
    </CustomCard>
  );
};

const CustomCard = styled(Card)`
  display: flex;
  flex-direction: row;
  gap: 26px;
  margin-bottom: 32px;
  background-color: ${tokens.BRAND_HEROBLUE};
  border-color: ${tokens.BRAND_HEROBLUE};
  padding: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex: 1;
  max-width: 460px;
  flex-direction: column;
  gap: 8px;
`;

const LocationContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

const CustomAutocomplete = styled(Autocomplete)`
  .sr-text-label {
    color: ${tokens.WHITE};
  }
`;

const CustomButton = styled(Button)`
  &.sr-button-primary {
    background: ${tokens.WHITE};
  }

  &.sr-button {
    color: ${tokens.BRAND_HERO_BLUE};
  }
`;

export { RetailAdvisorDashboard };
